import React, { useState, useEffect } from 'react';
import {
  C,
  LEADERBOARD_DATA_FOOTER_H,
  LEADERBOARD_DATA_HEADER_H,
  LEADERBOARD_DATA_ROW_GAP,
  LEADERBOARD_DATA_ROW_H,
  LEADERBOARD_DATA_W,
  getPathValue,
  imgUrl,
  normalizeImgField,
  nx,
  ny,
} from '../flowCanvas.constants';

const PAD = 14;
const IMAGE_SIZE = 42;
const ROW_INSET = 10;
const NAME_X = PAD + 62;
const SCORE_X = PAD + 256;
const IMAGE_LABEL_W = 40;
const IMAGE_TOGGLE_W = 74;
const ZERO_LABEL_W = 58;
const ZERO_FIELD_GAP = 8;
const ZERO_TOGGLE_W = 88;
const TOP_BAR_Y = 14;
const TOP_BAR_H = 42;
const TOP_BAR_DELETE_PAD = 20;
const DELETE_RESERVE_W = 38;
const DIVIDER_Y = 64;
const BOTTOM_CONTROL_Y = 76;
const BOTTOM_LABEL_Y = 92;

function clampNumber(value, min, max) {
  let nextValue = Number.isFinite(value) ? value : 0;
  if (typeof min === 'number') nextValue = Math.max(min, nextValue);
  if (typeof max === 'number') nextValue = Math.min(max, nextValue);
  return nextValue;
}

function splitTimeValue(totalSeconds) {
  const safeTotal = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safeTotal / 3600);
  const minutes = Math.floor((safeTotal % 3600) / 60);
  const seconds = Math.floor(safeTotal % 60);
  return { hours, minutes, seconds };
}

function TextInput({ x, y, width, value, placeholder, onChange, onActivate, selected = false, selectedColor = C.sel }) {
  return (
    <foreignObject x={x} y={y} width={width} height="28" style={{ pointerEvents: 'auto' }}>
      <input
        value={value}
        placeholder={placeholder}
        onClick={(event) => {
          event.stopPropagation();
          onActivate?.();
        }}
        onFocus={(event) => {
          event.stopPropagation();
          onActivate?.();
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          onChange?.(event.target.value);
        }}
        style={{
          width: '100%',
          height: '26px',
          borderRadius: '13px',
          border: selected ? `2px solid ${selectedColor}` : '1px solid rgba(255,255,255,0.14)',
          background: 'rgba(255,255,255,0.05)',
          color: '#ffffff',
          fontSize: '10px',
          fontWeight: 600,
          padding: '0 10px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </foreignObject>
  );
}

function NumberInput({ x, y, width, value, placeholder, min, max, onChange, onActivate, selected = false, selectedColor = C.sel }) {
  return (
    <foreignObject x={x} y={y} width={width} height="28" style={{ pointerEvents: 'auto' }}>
      <input
        type="number"
        value={value}
        placeholder={placeholder}
        min={min}
        max={max}
        onClick={(event) => {
          event.stopPropagation();
          onActivate?.();
        }}
        onFocus={(event) => {
          event.stopPropagation();
          onActivate?.();
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          const nextValue = event.target.value;
          onChange?.(nextValue === '' ? 0 : clampNumber(Number(nextValue), min, max));
        }}
        style={{
          width: '100%',
          height: '26px',
          borderRadius: '13px',
          border: selected ? `2px solid ${selectedColor}` : '1px solid rgba(255,255,255,0.14)',
          background: 'rgba(255,255,255,0.05)',
          color: '#ffffff',
          fontSize: '10px',
          fontWeight: 700,
          padding: '0 10px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </foreignObject>
  );
}

function SelectInput({ x, y, width, value, options, onChange, onActivate, selected = false, selectedColor = C.sel }) {
  return (
    <foreignObject x={x} y={y} width={width} height="28" style={{ pointerEvents: 'auto' }}>
      <select
        value={String(value)}
        onClick={(event) => {
          event.stopPropagation();
          onActivate?.();
        }}
        onFocus={(event) => {
          event.stopPropagation();
          onActivate?.();
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          onChange?.(Number(event.target.value));
        }}
        style={{
          width: '100%',
          height: '26px',
          borderRadius: '13px',
          border: selected ? `2px solid ${selectedColor}` : '1px solid rgba(255,255,255,0.14)',
          background: 'rgba(255,255,255,0.05)',
          color: '#ffffff',
          fontSize: '10px',
          fontWeight: 700,
          padding: '0 10px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} style={{ background: '#1a1f35', color: '#ffffff' }}>
            {option.label}
          </option>
        ))}
      </select>
    </foreignObject>
  );
}

function buildRangeOptions(min, max, padTo = 2) {
  return Array.from({ length: max - min + 1 }, (_, index) => {
    const value = min + index;
    return {
      value,
      label: String(value).padStart(padTo, '0'),
    };
  });
}

function TimeInput({ x, y, width, value, timeDisplay, onChange, onActivate, selected = false, selectedColor = C.sel }) {
  const { hours, minutes, seconds } = splitTimeValue(value);
  const normalizedDisplay = timeDisplay || 's';
  const hourOptions = buildRangeOptions(0, 23);
  const minuteOptions = buildRangeOptions(0, 59);
  const secondOptions = buildRangeOptions(0, 59);

  if (normalizedDisplay === 's') {
    return (
      <NumberInput
        x={x}
        y={y}
        width={width}
        value={Math.max(0, Number(value) || 0)}
        placeholder="Secs"
        min={0}
        selected={selected}
        selectedColor={selectedColor}
        onActivate={onActivate}
        onChange={(nextSeconds) => onChange?.(Math.max(0, Number(nextSeconds) || 0))}
      />
    );
  }

  const segments = normalizedDisplay === 'hms'
    ? [
      { key: 'hours', value: clampNumber(hours, 0, 23), options: hourOptions },
      { key: 'minutes', value: minutes, options: minuteOptions },
      { key: 'seconds', value: seconds, options: secondOptions },
    ]
    : normalizedDisplay === 'hm'
      ? [
        { key: 'hours', value: clampNumber(hours, 0, 23), options: hourOptions },
        { key: 'minutes', value: minutes, options: minuteOptions },
      ]
      : [
        { key: 'minutes', value: clampNumber(Math.floor(Math.max(0, Number(value) || 0) / 60), 0, 59), options: minuteOptions },
        { key: 'seconds', value: seconds, options: secondOptions },
      ];

  const gap = 6;
  const segmentWidth = (width - gap * (segments.length - 1)) / segments.length;

  const updateSegment = (segmentKey, segmentValue) => {
    const safeValue = Math.max(0, Number(segmentValue) || 0);
    if (normalizedDisplay === 'hms') {
      const nextHours = segmentKey === 'hours' ? clampNumber(safeValue, 0, 23) : clampNumber(hours, 0, 23);
      const nextMinutes = segmentKey === 'minutes' ? clampNumber(safeValue, 0, 59) : minutes;
      const nextSeconds = segmentKey === 'seconds' ? clampNumber(safeValue, 0, 59) : seconds;
      onChange?.((nextHours * 3600) + (nextMinutes * 60) + nextSeconds);
      return;
    }

    if (normalizedDisplay === 'hm') {
      const nextHours = segmentKey === 'hours' ? clampNumber(safeValue, 0, 23) : clampNumber(hours, 0, 23);
      const nextMinutes = segmentKey === 'minutes' ? clampNumber(safeValue, 0, 59) : minutes;
      onChange?.((nextHours * 3600) + (nextMinutes * 60));
      return;
    }

    const totalMinutes = segmentKey === 'minutes'
      ? safeValue
      : Math.floor(Math.max(0, Number(value) || 0) / 60);
    const nextSeconds = segmentKey === 'seconds' ? clampNumber(safeValue, 0, 59) : seconds;
    onChange?.((totalMinutes * 60) + nextSeconds);
  };

  return (
    <>
      {segments.map((segment, index) => (
        <SelectInput
          key={segment.key}
          x={x + index * (segmentWidth + gap)}
          y={y}
          width={segmentWidth}
          value={segment.value}
          options={segment.options}
          selected={selected}
          selectedColor={selectedColor}
          onActivate={onActivate}
          onChange={(nextValue) => updateSegment(segment.key, nextValue)}
        />
      ))}
    </>
  );
}

function ToggleChip({ x, y, width, active, label, activeColor, onClick }) {
  return (
    <g
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={x}
        y={y}
        width={width}
        height="24"
        rx="12"
        fill={active ? `${activeColor}22` : 'rgba(255,255,255,0.04)'}
        stroke={active ? activeColor : 'rgba(255,255,255,0.12)'}
        strokeWidth="1"
      />
      <text
        x={x + width / 2}
        y={y + 16}
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fill={active ? '#ffffff' : C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {label}
      </text>
    </g>
  );
}

function DeleteButton({ cx, cy, onClick }) {
  return (
    <g
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      style={{ cursor: 'pointer' }}
    >
      <circle cx={cx} cy={cy} r="10" fill="#dc2626" />
      <g transform={`translate(${cx}, ${cy})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
        <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
        <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
        <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
        <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
      </g>
    </g>
  );
}

function resolveEntries(step, liveCustomisationData) {
  const cfg = step?.customisationConfig;
  const tabKey = cfg?.tabKey;
  const varKey = cfg?.varKey;
  const varName = cfg?.variables?.[0]?.variableName;
  const liveValues = tabKey && varKey && varName
    ? getPathValue(liveCustomisationData, [tabKey, varKey, varName])
    : null;
  const allValues = Array.isArray(liveValues)
    ? liveValues
    : (step?.populatedData?.variables?.[0]?.allValues || []);
  return Array.isArray(allValues) ? allValues.filter(Boolean) : [];
}

function resolveSettings(liveCustomisationData) {
  const setup = getPathValue(liveCustomisationData, ['leaderboard', 'settings', 'setup']);
  return Array.isArray(setup) ? (setup[0] || {}) : {};
}

export default function LeaderboardDataNode({
  step,
  selected,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  campaignId,
  assetFolder,
  selectedFieldDataLink,
  activeEntryField,
  onActivate,
  onAddEntry,
  onEntryFieldChange,
  onDeleteEntry,
  onActivateEntryField,
}) {
  const [localActiveField, setLocalActiveField] = useState(null);
  useEffect(() => { if (!selected) setLocalActiveField(null); }, [selected]);

  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const entries = resolveEntries(step, liveCustomisationData);
  const settings = resolveSettings(liveCustomisationData);
  const scoreType = settings?.scoreType || 'points';
  const timeDisplay = settings?.timeDisplay || 's';
  const ascending = settings?.ascending === true;
  const sortedEntries = entries
    .map((entry, sourceIndex) => ({ entry, sourceIndex }))
    .sort((a, b) => {
      const aVal = scoreType === 'time' ? Number(a.entry?.time || 0) : Number(a.entry?.score || 0);
      const bVal = scoreType === 'time' ? Number(b.entry?.time || 0) : Number(b.entry?.score || 0);
      return ascending ? aVal - bVal : bVal - aVal;
    });
  const rowCount = Math.max(sortedEntries.length, 1);
  const bodyH = rowCount * (LEADERBOARD_DATA_ROW_H + LEADERBOARD_DATA_ROW_GAP) - LEADERBOARD_DATA_ROW_GAP;
  const nodeH = LEADERBOARD_DATA_HEADER_H + bodyH + LEADERBOARD_DATA_FOOTER_H + 24;
  const left = x - LEADERBOARD_DATA_W / 2;
  const top = y - nodeH / 2;

  return (
    <g className="fc-node fc-node--leaderboard-data" onClick={onActivate} style={{ cursor: 'pointer' }}>
      <rect x={left + 2} y={top + 4} width={LEADERBOARD_DATA_W} height={nodeH} rx="14" fill="#000" fillOpacity="0.28" />
      <rect
        x={left}
        y={top}
        width={LEADERBOARD_DATA_W}
        height={nodeH}
        rx="14"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.purple}
        strokeWidth={selected ? 2 : 1.4}
      />
      <line x1={left + 1} y1={top + LEADERBOARD_DATA_HEADER_H} x2={left + LEADERBOARD_DATA_W - 1} y2={top + LEADERBOARD_DATA_HEADER_H} stroke={C.border} strokeWidth="1" />

      <text x={left + PAD} y={top + 20} fontSize="12" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {step.stepTitle || 'Manual Leaderboard Data'}
      </text>
      <text x={left + PAD} y={top + 34} fontSize="9" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Preview order follows the current ascending / descending setting
      </text>

      {sortedEntries.length === 0 && (
        <text x={left + LEADERBOARD_DATA_W / 2} y={top + LEADERBOARD_DATA_HEADER_H + 34} textAnchor="middle" fontSize="10" fill={C.textDim} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          No leaderboard entries yet
        </text>
      )}

      {sortedEntries.map(({ entry, sourceIndex }, index) => {
        const rowTop = top + LEADERBOARD_DATA_HEADER_H + 12 + index * (LEADERBOARD_DATA_ROW_H + LEADERBOARD_DATA_ROW_GAP);
        const displayName = String(entry?.overrideName || entry?.name || '').trim();
        const scoreValue = scoreType === 'time' ? Number(entry?.time || 0) : Number(entry?.score || 0);
        const imageFieldDataLink = `leaderboard.leaderboardData.entries.${sourceIndex}.playerImage`;
        const imageField = normalizeImgField(entry?.playerImage);
        const imageSrc = imageField.srcUrl || imgUrl(imageField.name, imageField.isDefault, assetFolder, campaignId);
        const showImage = entry?.addPlayerImage === true;
        const showZeroCopy = entry?.showCopyZeroScore === true;
        const scoreFieldDataLink = `leaderboard.leaderboardData.entries.${sourceIndex}.${scoreType === 'time' ? 'time' : 'score'}`;
        const rowLeft = left + PAD;
        const rowWidth = LEADERBOARD_DATA_W - PAD * 2;
        const topBarX = rowLeft + 8;
        const topBarY = rowTop + TOP_BAR_Y;
        const topBarW = rowWidth - 16;
        const deleteCx = topBarX + topBarW - TOP_BAR_DELETE_PAD;
        const contentRight = deleteCx - DELETE_RESERVE_W;
        const positionBarX = topBarX + 8;
        const positionBarW = 34;
        const imageBoxX = positionBarX + positionBarW + 10;
        const nameInputX = imageBoxX + IMAGE_SIZE + 12;
        const timeInputW = timeDisplay === 'hms' ? 156 : 124;
        const scoreInputW = scoreType === 'time' ? timeInputW : 86;
        const scoreInputX = contentRight - scoreInputW;
        const nameInputW = Math.max(118, scoreInputX - nameInputX - 12);
        const imageLabelX = left + NAME_X;
        const imageToggleX = imageLabelX + IMAGE_LABEL_W + ZERO_FIELD_GAP;
        const zeroLabelX = imageToggleX + IMAGE_TOGGLE_W + 18;
        const zeroToggleX = zeroLabelX + ZERO_LABEL_W + ZERO_FIELD_GAP;
        const zeroFieldX = zeroToggleX + ZERO_TOGGLE_W + ZERO_FIELD_GAP;
        const zeroMessageWidth = deleteCx - 24 - zeroFieldX;
        const activateField = (field) => {
          setLocalActiveField({ itemIndex: sourceIndex, field });
          onActivateEntryField?.(sourceIndex, field);
        };

        return (
          <g
            key={`leaderboard-entry-${sourceIndex}`}
            onClick={(event) => {
              event.stopPropagation();
              activateField('overrideName');
            }}
            style={{ cursor: 'pointer' }}
          >
            <rect x={rowLeft} y={rowTop} width={rowWidth} height={LEADERBOARD_DATA_ROW_H} rx="12" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.10)" strokeWidth="1" />

            <rect
              x={topBarX}
              y={topBarY}
              width={topBarW}
              height={TOP_BAR_H}
              rx="11"
              fill="rgba(255,255,255,0.045)"
              stroke="rgba(255,255,255,0.10)"
              strokeWidth="1"
            />

            <rect
              x={positionBarX}
              y={topBarY + 4}
              width={positionBarW}
              height={TOP_BAR_H - 8}
              rx="9"
              fill="rgba(145,70,255,0.14)"
              stroke="rgba(145,70,255,0.30)"
              strokeWidth="1"
            />
            <text x={positionBarX + positionBarW / 2} y={topBarY + 17} textAnchor="middle" fontSize="8" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              POS
            </text>
            <text x={positionBarX + positionBarW / 2} y={topBarY + 31} textAnchor="middle" fontSize="12" fontWeight="800" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {index + 1}
            </text>

            <g
              onClick={(event) => {
                event.stopPropagation();
                if (!showImage) {
                  onEntryFieldChange?.(sourceIndex, 'addPlayerImage', true);
                }
                activateField('playerImage');
              }}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={imageBoxX}
                y={topBarY}
                width={IMAGE_SIZE}
                height={IMAGE_SIZE}
                rx="10"
                fill={showImage ? 'rgba(145,70,255,0.12)' : 'rgba(255,255,255,0.04)'}
                stroke={(localActiveField?.itemIndex === sourceIndex && localActiveField?.field === 'playerImage') ? C.sel : (showImage ? C.purple : 'rgba(255,255,255,0.14)')}
                strokeWidth={(localActiveField?.itemIndex === sourceIndex && localActiveField?.field === 'playerImage') ? 2 : 1}
                strokeDasharray={showImage ? 'none' : '4 3'}
              />
              {showImage && imageSrc ? (
                <image href={imageSrc} x={imageBoxX} y={topBarY} width={IMAGE_SIZE} height={IMAGE_SIZE} preserveAspectRatio="xMidYMid slice" clipPath={undefined} />
              ) : (
                <text x={imageBoxX + 21} y={topBarY + 25} textAnchor="middle" fontSize="9" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  IMG
                </text>
              )}
            </g>

            <TextInput
              x={nameInputX}
              y={topBarY + 7}
              width={nameInputW}
              value={displayName}
              placeholder="Display name"
              onActivate={() => activateField('overrideName')}
              onChange={(value) => onEntryFieldChange?.(sourceIndex, 'overrideName', value)}
            />

            {scoreType === 'time' ? (
              <TimeInput
                x={scoreInputX}
                y={topBarY + 7}
                width={scoreInputW}
                value={scoreValue}
                timeDisplay={timeDisplay}
                onActivate={() => activateField('time')}
                onChange={(value) => onEntryFieldChange?.(sourceIndex, 'time', value)}
              />
            ) : (
              <NumberInput
                x={scoreInputX}
                y={topBarY + 7}
                width={scoreInputW}
                value={scoreValue}
                placeholder="Score"
                onActivate={() => activateField('score')}
                onChange={(value) => onEntryFieldChange?.(sourceIndex, 'score', value)}
              />
            )}

            {localActiveField?.itemIndex === sourceIndex && localActiveField?.field === (scoreType === 'time' ? 'time' : 'score') && (
              <rect
                x={scoreInputX - 5}
                y={topBarY + 2}
                width={scoreInputW + 10}
                height="38"
                rx="19"
                fill="none"
                stroke={C.green}
                strokeWidth="2"
                pointerEvents="none"
              />
            )}

            <DeleteButton cx={deleteCx} cy={topBarY + TOP_BAR_H / 2} onClick={() => onDeleteEntry?.(sourceIndex)} />

            <line
              x1={rowLeft + 10}
              y1={rowTop + DIVIDER_Y}
              x2={rowLeft + rowWidth - 10}
              y2={rowTop + DIVIDER_Y}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
            />

            <text
              x={imageLabelX}
              y={rowTop + BOTTOM_LABEL_Y}
              fontSize="9"
              fontWeight="700"
              fill={C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              Image
            </text>
            <ToggleChip
              x={imageToggleX}
              y={rowTop + BOTTOM_CONTROL_Y}
              width={IMAGE_TOGGLE_W}
              active={showImage}
              label={showImage ? 'On' : 'Off'}
              activeColor={C.purple}
              onClick={() => {
                activateField('addPlayerImage');
                onEntryFieldChange?.(sourceIndex, 'addPlayerImage', !showImage);
              }}
            />

            <text
              x={zeroLabelX}
              y={rowTop + BOTTOM_LABEL_Y}
              fontSize="9"
              fontWeight="700"
              fill={C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              Zero Msg
            </text>
            <ToggleChip
              x={zeroToggleX}
              y={rowTop + BOTTOM_CONTROL_Y}
              width={ZERO_TOGGLE_W}
              active={showZeroCopy}
              label={showZeroCopy ? 'On' : 'Off'}
              activeColor={C.orange}
              onClick={() => {
                activateField('showCopyZeroScore');
                onEntryFieldChange?.(sourceIndex, 'showCopyZeroScore', !showZeroCopy);
              }}
            />

            {showZeroCopy && (
              <>
                <TextInput
                  x={zeroFieldX}
                  y={rowTop + BOTTOM_CONTROL_Y}
                  width={zeroMessageWidth}
                  value={entry?.copy || ''}
                  placeholder="Message shown when score is zero"
                  onActivate={() => activateField('copy')}
                  onChange={(value) => onEntryFieldChange?.(sourceIndex, 'copy', value)}
                />
              </>
            )}
          </g>
        );
      })}

      <g
        onClick={(event) => {
          event.stopPropagation();
          // Add a blank entry at the next index position
          onEntryFieldChange?.(entries.length, 'name', ' ');
        }}
        style={{ cursor: 'pointer' }}
      >
        <rect
          x={left + PAD}
          y={top + nodeH - LEADERBOARD_DATA_FOOTER_H}
          width={LEADERBOARD_DATA_W - PAD * 2}
          height="28"
          rx="14"
          fill="rgba(74,222,128,0.12)"
          stroke={C.green}
          strokeWidth="1"
          strokeDasharray="4 3"
        />
        <text
          x={left + LEADERBOARD_DATA_W / 2}
          y={top + nodeH - LEADERBOARD_DATA_FOOTER_H + 18}
          textAnchor="middle"
          fontSize="10"
          fontWeight="700"
          fill="#dcfce7"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          + Add leaderboard entry
        </text>
      </g>
    </g>
  );
}